import React, { createContext, useContext, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

class FileProgress extends File {
  id: string;
  progress: number;
  status: string;
  categories: string;
  visible_to: string[];
  cropped: boolean;
  fileSize: number; // renamed property to avoid conflict with inherited `size`
  fileType: string;

  constructor(file: File, isCropped = false) {
    // Create a new blob from the file to pass to the superclass
    const blob = new Blob([file], { type: file.type });
    super([blob], file.name, { type: file.type, lastModified: file.lastModified });

    Object.setPrototypeOf(this, FileProgress.prototype);

    this.id = uuidv4();
    this.progress = 0;
    this.status = "onProcess";
    this.categories = "";
    this.visible_to = [];
    this.cropped = isCropped;
    this.fileSize = file.size; // use fileSize instead of size
    this.fileType = this.extractFileType(file.name);
  }

  // Utility method to extract file type from filename extension
  private extractFileType(filename: string): string {
    const extensionMatch = filename.match(/\.([^.]+)$/);
    if (extensionMatch && extensionMatch[1]) {
      return extensionMatch[1].toLowerCase();
    }
    return "unknown";
  }

  // Methods for progress
  getProgress(): number {
    return this.progress;
  }

  setProgress(value: number): void {
    this.progress = value;
  }

  // Methods for status
  getStatus(): string {
    return this.status;
  }

  setStatus(value: string): void {
    this.status = value;
  }

  // Methods for categories
  getCategories(): string {
    return this.categories;
  }

  setCategories(value: string): void {
    this.categories = value;
  }

  // Methods for visible_to
  getVisibleTo(): string[] {
    return this.visible_to;
  }

  setVisibleTo(value: string[]): void {
    this.visible_to = value;
  }

  // Methods for cropped
  isCropped(): boolean {
    return this.cropped;
  }

  setCropped(value: boolean): void {
    this.cropped = value;
  }

  // Methods for fileSize
  getFileSize(): number {
    return this.fileSize;
  }

  setFileSize(value: number): void {
    this.fileSize = value;
  }

  // Methods for fileType
  getFileType(): string {
    return this.fileType;
  }

  setFileType(value: string): void {
    this.fileType = value;
  }
}

interface FileUploadContextType {
  files: FileProgress[];
  addFile: (file: File, isCropped?: boolean) => FileProgress;
  emptyFiles: () => void;
  setFileProgress: (id: string, progress: number) => void;
  setFileStatus: (id: string, status: string) => void;
  updateFileCategory: (id: string, categories: string) => void;
  updateFileVisibility: (id: string, visible_to: string[]) => void;
  removeFile: (id: string) => void;
}

const FileUploadContext = createContext<FileUploadContextType | undefined>(undefined);

export const useUpload = () => {
  const context = useContext(FileUploadContext);
  if (!context) {
    throw new Error("useUpload must be used within an UploadProvider");
  }
  return context;
};

export const FileUploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [files, setFiles] = useState<FileProgress[]>([]);

  const addFile = (file: File, isCropped = false): FileProgress => {
    const newFile = new FileProgress(file, isCropped);
    setFiles((prevFiles) => [...prevFiles, newFile]);
    return newFile;
  };

  const emptyFiles = () => {
    setFiles([]);
  };

  const setFileProgress = (id: string, progress: number): void => {
    setFiles((prevFiles) =>
      prevFiles.map((file) => {
        if (file.id === id) {
          file.setProgress(progress);
        }
        return file;
      })
    );
  };

  const setFileStatus = (id: string, status: string): void => {
    setFiles((prevFiles) =>
      prevFiles.map((file) => {
        if (file.id === id) {
          file.setStatus(status);
        }
        return file;
      })
    );
  };

  const updateFileCategory = (id: string, categories: string): void => {
    setFiles((prevFiles) =>
      prevFiles.map((file) => {
        if (file.id === id) {
          file.setCategories(categories);
        }
        return file;
      })
    );
  };

  const updateFileVisibility = (id: string, visible_to: string[]): void => {
    setFiles((prevFiles) =>
      prevFiles.map((file) => {
        if (file.id === id) {
          file.setVisibleTo(visible_to);
        }
        return file;
      })
    );
  };

  const removeFile = (id: string): void => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.id !== id));
  };

  const fileUploadValue = useMemo(
    () => ({
      files,
      addFile,
      emptyFiles,
      setFileProgress,
      setFileStatus,
      updateFileCategory,
      updateFileVisibility,
      removeFile
    }),
    [
      files,
      addFile,
      emptyFiles,
      setFileProgress,
      setFileStatus,
      updateFileCategory,
      updateFileVisibility,
      removeFile
    ]
  );

  return (
    <FileUploadContext.Provider value={fileUploadValue}>{children}</FileUploadContext.Provider>
  );
};
